import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import styles from "./PartnerHome.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import PartnerNavSlider from "./NavSlider/PartnerNavSlider";

/*
=========================================================
PARTNER HOME
=========================================================
*/

const API_BASE_URL = "https://coinyoco.world/brobizee-api";

const API_FETCH_URL = `${API_BASE_URL}/get_partner_profile.php`;
const API_UPDATE_URL = `${API_BASE_URL}/update_partner_profile.php`;
const API_DUTY_UPDATE_URL = `${API_BASE_URL}/update_partner_duty.php`;
const API_SENDER_REQUEST_URL = `${API_BASE_URL}/update_sender_request.php`;
const API_UPDATE_WORK_URL = `${API_BASE_URL}/update_partner_work.php`;
const API_SEND_OTP_URL = `${API_BASE_URL}/send_partner_otp.php`;

const DUTY_STORAGE_KEY = "partner_duty_state";

/* ⭐ AUTO CANCEL — Duration in seconds */
const AUTO_CANCEL_DURATION = 10;

/* =========================================================
   RINGTONE HOOK — LOUD ALARM STYLE (LIKE MORNING ALARM)
========================================================= */

const useRingtone = (shouldPlay) => {
  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);
  const masterGainRef = useRef(null);

  useEffect(() => {
    /* ---------- CLEANUP WHEN STOPPED ---------- */
    if (!shouldPlay) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch {
          /* ignore */
        }
        audioCtxRef.current = null;
      }
      masterGainRef.current = null;
      return;
    }

    let cancelled = false;

    /* --------------------------------------------------
       LOUD ALARM BEEP
    -------------------------------------------------- */
    const playAlarmBeep = (
      ctx,
      masterGain,
      freq,
      duration,
      delay = 0
    ) => {
      const startTime = ctx.currentTime + delay;

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();

      osc1.type = "square";
      osc1.frequency.value = freq;

      gain1.gain.setValueAtTime(0, startTime);
      gain1.gain.linearRampToValueAtTime(1, startTime + 0.01);
      gain1.gain.setValueAtTime(1, startTime + duration - 0.02);
      gain1.gain.linearRampToValueAtTime(0, startTime + duration);

      osc1.connect(gain1);
      gain1.connect(masterGain);

      osc1.start(startTime);
      osc1.stop(startTime + duration);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();

      osc2.type = "square";
      osc2.frequency.value = freq * 1.5;

      gain2.gain.setValueAtTime(0, startTime);
      gain2.gain.linearRampToValueAtTime(0.6, startTime + 0.01);
      gain2.gain.setValueAtTime(0.6, startTime + duration - 0.02);
      gain2.gain.linearRampToValueAtTime(0, startTime + duration);

      osc2.connect(gain2);
      gain2.connect(masterGain);

      osc2.start(startTime);
      osc2.stop(startTime + duration);
    };

    const playAlarmCycle = () => {
      if (cancelled) return;

      const ctx = audioCtxRef.current;
      const masterGain = masterGainRef.current;

      if (!ctx || !masterGain) return;

      playAlarmBeep(ctx, masterGain, 1000, 0.15, 0);
      playAlarmBeep(ctx, masterGain, 1000, 0.15, 0.2);
      playAlarmBeep(ctx, masterGain, 1000, 0.15, 0.4);
    };

    try {
      const AudioContext =
        window.AudioContext || window.webkitAudioContext;

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;

      const masterGain = ctx.createGain();
      masterGain.gain.value = 1.0;

      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -50;
      compressor.knee.value = 40;
      compressor.ratio.value = 20;
      compressor.attack.value = 0;
      compressor.release.value = 0.25;

      masterGain.connect(compressor);
      compressor.connect(ctx.destination);

      masterGainRef.current = masterGain;

      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }

      playAlarmCycle();
      intervalRef.current = setInterval(playAlarmCycle, 1200);
    } catch (err) {
      console.warn("Ringtone failed:", err);
    }

    return () => {
      cancelled = true;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch {
          /* ignore */
        }
        audioCtxRef.current = null;
      }

      masterGainRef.current = null;
    };
  }, [shouldPlay]);
};

/* =========================================================
   NOTIFICATION PERMISSION HOOK
========================================================= */

const useNotificationPermission = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined"
      ? Notification.permission
      : "denied"
  );

  useEffect(() => {
    if (typeof Notification === "undefined") return;

    if (Notification.permission === "default") {
      const timer = setTimeout(() => {
        Notification.requestPermission()
          .then((perm) => {
            console.log("Notification permission:", perm);
            setPermission(perm);
          })
          .catch((err) => {
            console.warn(
              "Notification permission error:",
              err
            );
          });
      }, 2000);

      return () => clearTimeout(timer);
    }

    setPermission(Notification.permission);
  }, []);

  return permission;
};

/* =========================================================
   SENDER NOTIFICATION HOOK
========================================================= */

const useSenderNotification = (
  senderPopupOpen,
  senderData,
  makeImageUrl
) => {
  const lastNotifiedRef = useRef("");

  useEffect(() => {
    if (!senderPopupOpen) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    const senderKey = `${senderData.name || ""}_${
      senderData.number || ""
    }`;

    if (!senderKey || senderKey === "_") return;
    if (senderKey === lastNotifiedRef.current) return;

    lastNotifiedRef.current = senderKey;

    try {
      const title = "🔔 New Request from BroBizee";
      const body = `👤 ${
        senderData.name || "Unknown Sender"
      }\n📞 ${senderData.number || "No number"}\n📍 ${
        senderData.address || "No address"
      }`;

      const iconUrl =
        senderData.profile_images &&
        makeImageUrl(senderData.profile_images);

      const notification = new Notification(title, {
        body,
        icon: iconUrl || undefined,
        badge: "/favicon.ico",
        tag: `sender-${senderKey}`,
        requireInteraction: true,
        vibrate: [
          400, 200, 400, 200, 400, 200, 400,
        ],
        silent: false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setTimeout(() => {
        try {
          notification.close();
        } catch {
          /* ignore */
        }
      }, 30000);
    } catch (err) {
      console.warn("Notification failed:", err);
    }
  }, [senderPopupOpen, senderData, makeImageUrl]);

  useEffect(() => {
    if (!senderPopupOpen) {
      lastNotifiedRef.current = "";
    }
  }, [senderPopupOpen]);
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function PartnerHome({ onBack, onUpdate }) {
  const navigate = useNavigate();

  /* SAVE TIMER */
  const [saveTimer, setSaveTimer] = useState(180);
  const [saveLocked, setSaveLocked] = useState(false);

  useEffect(() => {
    if (!saveLocked) return;

    const timer = setInterval(() => {
      setSaveTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setSaveLocked(false);
          return 180;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [saveLocked]);

  /* PARAMS */
  const params = useParams();

  const userId = params.userId || params.userIdA || "";
  const uuid = params.uuid || params.deviceUuid || "";

  /* BASIC STATES */
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  /* FORM DATA */
  const [formData, setFormData] = useState({
    user_id: userId || "",
    uuid: uuid || "",

    full_name: "",
    phone_number: "",
    email_id: "",
    address: "",

    latitude_longitude: "",

    age: "",
    gender: "Male",
    bio: "",

    work: "",
    experiences: "",

    document_type: "Aadhar",

    account_type: "Savings",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",

    refrences: "",

    recently_work: "",
    secondary_work: "",
  });

  /* FILE STATES */
  const [files, setFiles] = useState({
    profile: null,
    upload_documents_images1: null,
    upload_documents_images2: null,
  });

  /* IMAGE PREVIEWS */
  const [previews, setPreviews] = useState({
    profile: "",
    upload_documents_images1: "",
    upload_documents_images2: "",
  });

  /* DUTY STATE */
  const [dutyState, setDutyState] = useState("off");

  /* ACTIVE TAB */
  const [activeTab, setActiveTab] = useState("home");

  /* JOB */
  const [jobType, setJobType] = useState("Carpenter");

  /* ⭐ JOB DROPUP */
  const [jobDropupOpen, setJobDropupOpen] = useState(false);
  const [jobUpdating, setJobUpdating] = useState(false);

  /* ⭐ Services list */
  const jobOptions = [
    "Carpenter",
    "Electrician",
    "Plumber",
    "Painter",
    "Mechanic",
    "Maid",
    "Driver",
    "Cook",
    "Gardener",
    "AC Technician",
    "Welder",
    "Mason",
  ];

  /* PROFIT */
  const [profit, setProfit] = useState(0);

  /* WORK */
  const [work, setWork] = useState("");

  /* SENDER NUMBER */
  const [senderNumber, setSenderNumber] = useState("");

  /* FULL SENDER DATA */
  const [senderData, setSenderData] = useState({
    profile_images: "",
    name: "",
    number: "",
    money: 0,
    address: "",
    lat_long: "",
    chatbox: "",
    request: "",
  });

  /* SENDER POPUP VISIBILITY */
  const [senderPopupOpen, setSenderPopupOpen] = useState(false);

  /* SWIPE PROGRESS */
  const [swipeLeft, setSwipeLeft] = useState(0);
  const [swipeRight, setSwipeRight] = useState(0);

  /* ⭐ AUTO-CANCEL TIMER — 10 seconds */
  const [autoCancelTimer, setAutoCancelTimer] = useState(
    AUTO_CANCEL_DURATION
  );
  const [autoCancelActive, setAutoCancelActive] = useState(false);

  /* MENU */
  const [menuOpen, setMenuOpen] = useState(false);

  /* LOCATION STATE */
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    loading: true,
  });

  const [locationError, setLocationError] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);

  /* MAP */
  const [mapLoading, setMapLoading] = useState(true);
  const [mapKey, setMapKey] = useState(0);

  /* REFS */
  const watchIdRef = useRef(null);
  const mountedRef = useRef(false);
  const previewUrlsRef = useRef([]);
  const dutyRequestRef = useRef(false);
  const autoRestoreRef = useRef(false);
  const otpStoredRef = useRef(false);

  /* ⭐ Auto-cancel refs */
  const autoCancelIntervalRef = useRef(null);
  const autoCancelTriggeredRef = useRef(false);
  const handleSenderCancelRef = useRef(null);

  /* BASE PATH */
  const basePath =
    userId && uuid ? `/${userId}/${uuid}` : "";

  /* RINGTONE */
  useRingtone(senderPopupOpen);

  /* NOTIFICATION PERMISSION */
  useNotificationPermission();

  /* NAVIGATION HELPER */
  const goTo = useCallback(
    (path) => {
      if (basePath) {
        navigate(`${basePath}${path}`);
      } else {
        navigate(path);
      }
    },
    [basePath, navigate]
  );

  /* IMAGE URL HELPER */
  const makeImageUrl = useCallback((path) => {
    if (!path) return "";

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("blob:")
    ) {
      return path;
    }

    const cleanPath = String(path).replace(/^\/+/, "");

    return `${API_BASE_URL}/${cleanPath}`;
  }, []);

  /* SENDER SYSTEM NOTIFICATION */
  useSenderNotification(
    senderPopupOpen,
    senderData,
    makeImageUrl
  );

  /* =========================================================
     MOBILE VIBRATION ON SENDER POPUP
  ========================================================= */
  useEffect(() => {
    if (!senderPopupOpen) return;
    if (!("vibrate" in navigator)) return;

    const vibratePattern = [
      400, 200, 400, 200, 400, 200, 400,
    ];

    try {
      navigator.vibrate(vibratePattern);
    } catch {
      /* ignore */
    }

    const vibrateInterval = setInterval(() => {
      try {
        navigator.vibrate(vibratePattern);
      } catch {
        /* ignore */
      }
    }, 2500);

    return () => {
      clearInterval(vibrateInterval);
      try {
        navigator.vibrate(0);
      } catch {
        /* ignore */
      }
    };
  }, [senderPopupOpen]);

  /* =========================================================
     UNLOCK AUDIO ON FIRST USER INTERACTION
  ========================================================= */
  useEffect(() => {
    const unlock = () => {
      try {
        const AudioContext =
          window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContext();
        ctx.resume()
          .then(() => ctx.close())
          .catch(() => {});
      } catch {
        /* ignore */
      }

      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown", unlock);
    };

    document.addEventListener("click", unlock);
    document.addEventListener("touchstart", unlock);
    document.addEventListener("keydown", unlock);

    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  /* SET PARTNER DATA */
  const setPartnerData = useCallback(
    (d) => {
      if (!d) return;

      setFormData({
        user_id: d.user_id || userId || "",
        uuid: d.uuid || d.secondary_uuid || uuid || "",

        full_name: d.full_name || "",
        phone_number: d.phone_number || "",
        email_id: d.email_id || "",
        address: d.address || "",

        latitude_longitude: d.latitude_longitude || "",

        age: d.age ?? "",
        gender: d.gender || "Male",
        bio: d.bio || "",

        work: d.work || "",
        experiences: d.experiences || "",

        document_type: d.document_type || "Aadhar",

        account_type: d.account_type || "Savings",
        account_holder_name: d.account_holder_name || "",
        account_number: d.account_number || "",
        ifsc_code: d.ifsc_code || "",
        upi_id: d.upi_id || "",

        refrences: d.refrences || "",
        recently_work: d.recently_work || "",
        secondary_work: d.secondary_work || "",
      });

      setPreviews({
        profile: makeImageUrl(d.profile_url || d.profile || ""),
        upload_documents_images1: makeImageUrl(
          d.upload_documents_images1_url ||
            d.upload_documents_images1 ||
            ""
        ),
        upload_documents_images2: makeImageUrl(
          d.upload_documents_images2_url ||
            d.upload_documents_images2 ||
            ""
        ),
      });

      setFiles({
        profile: null,
        upload_documents_images1: null,
        upload_documents_images2: null,
      });

      const serverStatus = String(
        d.online_stutes || "Offline"
      ).trim();

      let finalState = "off";

      if (serverStatus === "Online") {
        finalState = "on";
      } else if (serverStatus === "IN Work") {
        finalState = "goto";
      }

      try {
        const savedState = localStorage.getItem(DUTY_STORAGE_KEY);

        if (
          savedState &&
          ["on", "off", "goto"].includes(savedState)
        ) {
          finalState = savedState;
        }
      } catch {
        /* ignore */
      }

      setProfit(Number(d.profit) || 0);

      setWork(d.work ? String(d.work).trim() : "");

      setSenderNumber(
        d.sender_number ? String(d.sender_number).trim() : ""
      );

      const newSenderData = {
        profile_images: d.sender_profile_images || "",
        name: d.sender_name || "",
        number: d.sender_number || "",
        money: Number(d.sender_money) || 0,
        address: d.sender_address || "",
        lat_long: d.sender_lat_long || "",
        chatbox: d.sender_chatbox || "",
        request: d.sender_request || "",
      };

      setSenderData(newSenderData);

      const hasSender =
        (newSenderData.name &&
          newSenderData.name.trim() !== "") ||
        (newSenderData.number &&
          newSenderData.number.trim() !== "");

      if (hasSender) {
        setSenderPopupOpen(true);
      }

      setDutyState(finalState);
    },
    [userId, uuid, makeImageUrl]
  );

  /* FETCH PARTNER PROFILE */
  const fetchPartnerProfile = useCallback(async () => {
    if (!userId || !uuid) {
      throw new Error("User ID or UUID is missing from URL.");
    }

    const url =
      `${API_FETCH_URL}` +
      `?user_id=${encodeURIComponent(userId)}` +
      `&uuid=${encodeURIComponent(uuid)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`Server returned HTTP ${response.status}.`);
    }

    let result;

    try {
      result = await response.json();
    } catch {
      throw new Error("PHP API ne valid JSON response nahi diya.");
    }

    if (!result || !result.success) {
      throw new Error(
        result?.message || "Partner profile not found."
      );
    }

    if (result.data) {
      setPartnerData(result.data);
    }

    return result.data;
  }, [userId, uuid, setPartnerData]);

  /* LOAD PROFILE */
  useEffect(() => {
    let cancelled = false;

    mountedRef.current = true;

    const loadData = async () => {
      if (!userId || !uuid) {
        setMessage({
          type: "error",
          text: "User ID or UUID is missing from URL.",
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setMessage({ type: "", text: "" });
        await fetchPartnerProfile();
      } catch (error) {
        console.error("Load partner profile error:", error);

        if (!cancelled) {
          setMessage({
            type: "error",
            text:
              error.message ||
              "Server se connection nahi ho raha.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [userId, uuid, fetchPartnerProfile]);

  /* AUTO REFRESH (10 sec) */
  useEffect(() => {
    if (!userId || !uuid) return;

    const refreshInterval = setInterval(() => {
      fetchPartnerProfile().catch((error) => {
        console.warn("Auto refresh failed:", error);
      });
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, [userId, uuid, fetchPartnerProfile]);

  /* =========================================================
     AUTO-STORE OTP — ONE TIME ONLY
  ========================================================= */
  useEffect(() => {
    if (loading) return;
    if (!userId || !uuid) return;

    if (otpStoredRef.current) return;

    const storageKey = `otp_stored_${userId}_${uuid}`;

    try {
      if (sessionStorage.getItem(storageKey) === "1") {
        otpStoredRef.current = true;
        return;
      }
    } catch {
      /* ignore */
    }

    otpStoredRef.current = true;

    const storeOtp = async () => {
      try {
        const payload = new FormData();
        payload.append("user_id", String(userId));
        payload.append("uuid", String(uuid));

        const response = await fetch(API_SEND_OTP_URL, {
          method: "POST",
          body: payload,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (!result?.success) {
          console.warn(
            "Auto OTP store failed:",
            result?.message
          );
          return;
        }

        console.log("✅ OTP auto-stored (4-digit):", result.otp);

        try {
          sessionStorage.setItem(storageKey, "1");
        } catch {
          /* ignore */
        }
      } catch (error) {
        console.warn("Auto OTP store error:", error);
      }
    };

    storeOtp();
  }, [loading, userId, uuid]);

  /* INPUT CHANGE */
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /* FILE CHANGE */
  const handleFileChange = (event) => {
    const { name, files: selectedFiles } = event.target;

    if (!selectedFiles || !selectedFiles[0]) return;

    const selectedFile = selectedFiles[0];

    if (!selectedFile.type.startsWith("image/")) {
      setMessage({
        type: "error",
        text: "Please select a valid image file.",
      });
      return;
    }

    if (previews[name]?.startsWith("blob:")) {
      URL.revokeObjectURL(previews[name]);
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    previewUrlsRef.current.push(previewUrl);

    setFiles((previous) => ({
      ...previous,
      [name]: selectedFile,
    }));

    setPreviews((previous) => ({
      ...previous,
      [name]: previewUrl,
    }));

    setMessage({ type: "", text: "" });
  };

  /* REMOVE FILE */
  const removeSelectedFile = (fieldName) => {
    setFiles((previous) => ({
      ...previous,
      [fieldName]: null,
    }));

    setPreviews((previous) => {
      const oldUrl = previous[fieldName];

      if (oldUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(oldUrl);
      }

      return {
        ...previous,
        [fieldName]: oldUrl?.startsWith("blob:") ? "" : oldUrl,
      };
    });

    const fileInput = document.querySelector(
      `input[name="${fieldName}"]`
    );

    if (fileInput) fileInput.value = "";
  };

  /* FORM VALIDATION */
  const validateForm = () => {
    if (!userId) return "User ID is missing.";
    if (!uuid) return "UUID is missing.";

    if (!formData.full_name.trim())
      return "Full name is required.";

    if (!formData.phone_number.trim())
      return "Phone number is required.";

    const phone = formData.phone_number.replace(/\D/g, "");

    if (phone.length < 10)
      return "Please enter a valid phone number.";

    if (
      formData.email_id.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email_id.trim()
      )
    ) {
      return "Please enter a valid email address.";
    }

    if (!formData.address.trim())
      return "Address is required.";

    if (formData.age !== "") {
      const numericAge = Number(formData.age);
      if (
        !Number.isFinite(numericAge) ||
        numericAge < 1 ||
        numericAge > 100
      ) {
        return "Please enter a valid age.";
      }
    }

    return "";
  };

  /* UPDATE PROFILE */
  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setMessage({ type: "error", text: validationError });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const payload = new FormData();

      payload.append("user_id", String(userId));
      payload.append("uuid", String(uuid));

      payload.append("full_name", formData.full_name.trim());
      payload.append("phone_number", formData.phone_number.trim());
      payload.append("email_id", formData.email_id.trim());
      payload.append("address", formData.address.trim());
      payload.append(
        "latitude_longitude",
        formData.latitude_longitude.trim()
      );

      payload.append(
        "age",
        formData.age === "" ? "" : String(formData.age)
      );

      payload.append("gender", formData.gender);
      payload.append("bio", formData.bio.trim());
      payload.append("work", formData.work.trim());
      payload.append("experiences", formData.experiences.trim());
      payload.append("recently_work", formData.recently_work.trim());
      payload.append(
        "secondary_work",
        formData.secondary_work.trim()
      );
      payload.append("document_type", formData.document_type);
      payload.append("account_type", formData.account_type);
      payload.append(
        "account_holder_name",
        formData.account_holder_name.trim()
      );
      payload.append(
        "account_number",
        formData.account_number.trim()
      );
      payload.append("ifsc_code", formData.ifsc_code.trim());
      payload.append("upi_id", formData.upi_id.trim());
      payload.append("refrences", formData.refrences.trim());

      if (files.profile) {
        payload.append("profile", files.profile);
      }

      if (files.upload_documents_images1) {
        payload.append(
          "upload_documents_images1",
          files.upload_documents_images1
        );
      }

      if (files.upload_documents_images2) {
        payload.append(
          "upload_documents_images2",
          files.upload_documents_images2
        );
      }

      const response = await fetch(API_UPDATE_URL, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error(
          `Server returned HTTP ${response.status}.`
        );
      }

      let result;

      try {
        result = await response.json();
      } catch {
        throw new Error(
          "Update API ne valid JSON response nahi diya."
        );
      }

      if (!result?.success) {
        throw new Error(
          result?.message || "Failed to update partner profile."
        );
      }

      setMessage({
        type: "success",
        text: "Partner profile updated successfully.",
      });

      if (typeof onUpdate === "function") {
        onUpdate({
          ...formData,
          user_id: userId,
          uuid: uuid,
          profile: previews.profile,
        });
      }

      try {
        await fetchPartnerProfile();
      } catch (refreshError) {
        console.warn("Profile refresh failed:", refreshError);
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Update partner profile error:", error);

      setMessage({
        type: "error",
        text:
          error.message ||
          "Server error occurred while updating profile.",
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  /* GPS ERROR */
  const handleLocationError = useCallback((error) => {
    console.error("Location Error:", error);

    if (!mountedRef.current) return;

    let errorMessage = "Live location detect nahi ho rahi hai.";

    if (error?.code === 1) {
      errorMessage =
        "Live location ke liye browser location permission Allow karein.";
    } else if (error?.code === 2) {
      errorMessage = "Aapki current location available nahi hai.";
    } else if (error?.code === 3) {
      errorMessage =
        "Location detect karne mein timeout hua. GPS check karein.";
    }

    setLocationError(errorMessage);

    setLocation((previous) => ({
      ...previous,
      error: error?.message || errorMessage,
      loading: false,
    }));

    setMapLoading(false);
  }, []);

  /* UPDATE LOCAL GPS */
  const updateLiveLocation = useCallback((position) => {
    if (!mountedRef.current) return;

    const { latitude, longitude, accuracy } = position.coords;

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) {
      setLocationError("Valid location coordinates nahi mile.");
      return;
    }

    const safeAccuracy = Number.isFinite(accuracy)
      ? accuracy
      : null;

    setLocation({
      latitude,
      longitude,
      accuracy: safeAccuracy,
      error: null,
      loading: false,
    });

    setCurrentLocation({
      lat: latitude,
      lng: longitude,
      accuracy: safeAccuracy || 0,
    });

    const coordinates = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

    setFormData((previous) => ({
      ...previous,
      latitude_longitude: coordinates,
    }));

    setMapKey((previous) => previous + 1);
    setMapLoading(false);
    setLocationError("");
  }, []);

  /* UPDATE GPS + STATUS TO DATABASE */
  const updateDutyStatus = useCallback(
    async (status, position = null) => {
      if (!userId || !uuid) {
        setMessage({
          type: "error",
          text: "User ID or UUID missing.",
        });
        return false;
      }

      let latitudeLongitude =
        formData.latitude_longitude || "";

      if (position?.coords) {
        const latitude = Number(position.coords.latitude);
        const longitude = Number(position.coords.longitude);

        if (
          Number.isFinite(latitude) &&
          Number.isFinite(longitude)
        ) {
          latitudeLongitude = `${latitude.toFixed(
            6
          )},${longitude.toFixed(6)}`;
        }
      }

      if (dutyRequestRef.current) return false;

      dutyRequestRef.current = true;

      try {
        const payload = new FormData();

        payload.append("user_id", String(userId));
        payload.append("uuid", String(uuid));
        payload.append("online_stutes", status);
        payload.append(
          "latitude_longitude",
          latitudeLongitude
        );

        const response = await fetch(API_DUTY_UPDATE_URL, {
          method: "POST",
          body: payload,
        });

        if (!response.ok) {
          throw new Error(`Duty API HTTP ${response.status}`);
        }

        let result;

        try {
          result = await response.json();
        } catch {
          throw new Error(
            "Duty API ne valid JSON response nahi diya."
          );
        }

        if (!result?.success) {
          throw new Error(
            result?.message || "Duty status update failed."
          );
        }

        setFormData((previous) => ({
          ...previous,
          latitude_longitude:
            result?.data?.latitude_longitude ||
            latitudeLongitude,
        }));

        return true;
      } catch (error) {
        console.error("Duty status update error:", error);

        setMessage({
          type: "error",
          text:
            error.message ||
            "Duty status database mein update nahi ho saka.",
        });

        return false;
      } finally {
        dutyRequestRef.current = false;
      }
    },
    [userId, uuid, formData.latitude_longitude]
  );

  /* UPDATE ONLY CURRENT GPS WHILE ONLINE */
  const updateDutyLocationOnly = useCallback(
    async (position) => {
      if (!userId || !uuid) return;

      const { latitude, longitude } = position.coords;

      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude)
      ) {
        return;
      }

      const coordinates = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

      updateLiveLocation(position);

      try {
        const payload = new FormData();

        payload.append("user_id", String(userId));
        payload.append("uuid", String(uuid));
        payload.append("online_stutes", "Online");
        payload.append("latitude_longitude", coordinates);

        const response = await fetch(API_DUTY_UPDATE_URL, {
          method: "POST",
          body: payload,
        });

        if (!response.ok) {
          console.warn(
            "GPS database update failed:",
            response.status
          );
          return;
        }

        const result = await response.json();

        if (!result?.success) {
          console.warn(
            "GPS database update failed:",
            result?.message
          );
        }
      } catch (error) {
        console.warn(
          "Continuous GPS database update error:",
          error
        );
      }
    },
    [userId, uuid, updateLiveLocation]
  );

  /* STOP LIVE LOCATION */
  const stopLiveLocation = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setCurrentLocation(null);
    setLocationError("");
  }, []);

  /* START LIVE LOCATION */
  const startLiveLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(
        "Your browser does not support live location."
      );
      return;
    }

    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" || hostname === "127.0.0.1";
    const isHttps = window.location.protocol === "https:";

    if (!isLocalhost && !isHttps) {
      setLocationError(
        "Live location ke liye HTTPS required hai."
      );
      return;
    }

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setLocationError("");
    setMapLoading(true);

    setLocation((previous) => ({
      ...previous,
      loading: true,
      error: null,
    }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        updateLiveLocation(position);
        await updateDutyLocationOnly(position);
      },
      (error) => {
        handleLocationError(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      }
    );

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        await updateDutyLocationOnly(position);
      },
      (error) => {
        handleLocationError(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      }
    );
  }, [
    updateLiveLocation,
    updateDutyLocationOnly,
    handleLocationError,
  ]);

  /* INITIAL GPS */
  useEffect(() => {
    mountedRef.current = true;

    if (!navigator.geolocation) {
      setLocation({
        latitude: null,
        longitude: null,
        accuracy: null,
        error: "Geolocation browser me support nahi karta.",
        loading: false,
      });

      setMapLoading(false);

      return () => {
        mountedRef.current = false;
      };
    }

    const detectInitialLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updateLiveLocation(position);
        },
        (error) => {
          handleLocationError(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    };

    detectInitialLocation();

    return () => {
      mountedRef.current = false;

      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }

      previewUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      });

      previewUrlsRef.current = [];
    };
  }, [updateLiveLocation, handleLocationError]);

  /* IF DATABASE STATUS IS ALREADY ONLINE */
  useEffect(() => {
    if (loading || dutyState !== "on") return;

    startLiveLocation();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [loading, dutyState, startLiveLocation]);

  /* ON DUTY */
  const handleOnDuty = useCallback(async () => {
    if (dutyRequestRef.current) return;

    if (!navigator.geolocation) {
      setLocationError(
        "Your browser does not support live location."
      );
      return;
    }

    setMessage({ type: "", text: "" });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        updateLiveLocation(position);

        const success = await updateDutyStatus(
          "Online",
          position
        );

        if (!success) return;

        setDutyState("on");

        try {
          localStorage.setItem(DUTY_STORAGE_KEY, "on");
        } catch {
          /* ignore */
        }

        startLiveLocation();
      },
      (error) => {
        handleLocationError(error);
        setMessage({
          type: "error",
          text:
            "ON DUTY ke liye current location permission required hai.",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      }
    );
  }, [
    updateLiveLocation,
    updateDutyStatus,
    startLiveLocation,
    handleLocationError,
  ]);

  /* OFF DUTY */
  const handleOffDuty = useCallback(async () => {
    if (dutyRequestRef.current) return;

    stopLiveLocation();

    const success = await updateDutyStatus("Offline");

    if (!success) return;

    setDutyState("off");

    try {
      localStorage.setItem(DUTY_STORAGE_KEY, "off");
    } catch {
      /* ignore */
    }

    setMessage({
      type: "success",
      text: "You're now Offline.\nBye for now! See you again. 👋",
    });
  }, [stopLiveLocation, updateDutyStatus]);

  /* GO TO WORK */
  const handleGoToWork = useCallback(async () => {
    if (dutyRequestRef.current) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          updateLiveLocation(position);

          const success = await updateDutyStatus(
            "IN Work",
            position
          );

          if (!success) return;

          stopLiveLocation();

          setDutyState("goto");

          try {
            localStorage.setItem(DUTY_STORAGE_KEY, "goto");
          } catch {
            /* ignore */
          }

          if (
            !window.location.pathname.includes("calling_noti")
          ) {
            goTo("/calling_noti");
          }
        },
        (error) => {
          handleLocationError(error);
          setMessage({
            type: "error",
            text:
              "Work start karne ke liye current location required hai.",
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      );
    } else {
      setMessage({
        type: "error",
        text: "Browser location support nahi karta.",
      });
    }
  }, [
    updateLiveLocation,
    updateDutyStatus,
    stopLiveLocation,
    goTo,
    handleLocationError,
  ]);

  /* AUTO RESTORE LAST DUTY STATE */
  useEffect(() => {
    if (loading) return;
    if (autoRestoreRef.current) return;
    if (!userId || !uuid) return;

    autoRestoreRef.current = true;

    let savedState = null;

    try {
      savedState = localStorage.getItem(DUTY_STORAGE_KEY);
    } catch {
      return;
    }

    if (
      !savedState ||
      !["on", "off", "goto"].includes(savedState)
    ) {
      return;
    }

    const timer = setTimeout(() => {
      if (savedState === "on") {
        handleOnDuty();
      } else if (savedState === "goto") {
        handleGoToWork();
      } else if (savedState === "off") {
        setDutyState("off");
        stopLiveLocation();
      }
    }, 800);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, userId, uuid]);

  /* =======================================================
     UPDATE SENDER REQUEST (API CALL)
  ======================================================= */

  const updateSenderRequest = useCallback(
    async (requestValue) => {
      if (!userId || !uuid) {
        console.warn(
          "User ID or UUID missing — cannot update sender_request."
        );
        return false;
      }

      const partnerSecondaryId =
        formData?.user_id || userId || "";

      const partnerSecondaryUuid =
        formData?.uuid || uuid || "";

      if (!partnerSecondaryId || !partnerSecondaryUuid) {
        console.warn(
          "Partner secondary_id or uuid missing — cannot update sender_request."
        );
        return false;
      }

      try {
        const payload = new FormData();

        payload.append(
          "partner_secondary_id",
          String(partnerSecondaryId)
        );

        payload.append(
          "partner_secondary_uuid",
          String(partnerSecondaryUuid)
        );

        payload.append(
          "sender_request",
          String(requestValue)
        );

        const response = await fetch(
          API_SENDER_REQUEST_URL,
          {
            method: "POST",
            body: payload,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        console.log("UPDATE SENDER REQUEST RESULT:", result);

        if (!result?.success) {
          console.warn(
            "Sender request update failed:",
            result?.message
          );
          return false;
        }

        console.log(
          `✅ sender_request set to '${requestValue}'`
        );

        setSenderData((prev) => ({
          ...prev,
          request: String(requestValue),
        }));

        return true;
      } catch (error) {
        console.error(
          "Update sender_request error:",
          error
        );
        return false;
      }
    },
    [userId, uuid, formData.user_id, formData.uuid]
  );

  /* =======================================================
     SENDER POPUP — Swipe Handlers
  ======================================================= */

  const handleCancelSwipeStart = (e) => {
    const startX = e.touches?.[0]?.clientX ?? e.clientX;

    const onMove = (ev) => {
      const currentX =
        ev.touches?.[0]?.clientX ?? ev.clientX;
      const diff = startX - currentX;
      const progress = Math.min(Math.max(diff / 120, 0), 1);
      setSwipeLeft(progress);
    };

    const onEnd = () => {
      setSwipeLeft((prev) => {
        if (prev >= 0.8) handleSenderCancel();
        return 0;
      });
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, {
      passive: false,
    });
    document.addEventListener("touchend", onEnd);
  };

  const handleAcceptSwipeStart = (e) => {
    const startX = e.touches?.[0]?.clientX ?? e.clientX;

    const onMove = (ev) => {
      const currentX =
        ev.touches?.[0]?.clientX ?? ev.clientX;
      const diff = currentX - startX;
      const progress = Math.min(Math.max(diff / 120, 0), 1);
      setSwipeRight(progress);
    };

    const onEnd = () => {
      setSwipeRight((prev) => {
        if (prev >= 0.8) handleSenderAccept();
        return 0;
      });
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, {
      passive: false,
    });
    document.addEventListener("touchend", onEnd);
  };

  /* SENDER CANCEL — Set sender_request = '3' */
  const handleSenderCancel = useCallback(async () => {
    /* ⭐ Stop auto-cancel timer */
    if (autoCancelIntervalRef.current) {
      clearInterval(autoCancelIntervalRef.current);
      autoCancelIntervalRef.current = null;
    }
    autoCancelTriggeredRef.current = true;
    setAutoCancelActive(false);

    setSenderPopupOpen(false);
    setSwipeLeft(0);
    setSwipeRight(0);

    await updateSenderRequest("3");

    setSenderData({
      profile_images: "",
      name: "",
      number: "",
      money: 0,
      address: "",
      lat_long: "",
      chatbox: "",
      request: "3",
    });

    setSenderNumber("");
  }, [updateSenderRequest]);

  /* ⭐ KEEP REF IN SYNC — so auto-cancel can call latest */
  useEffect(() => {
    handleSenderCancelRef.current = handleSenderCancel;
  }, [handleSenderCancel]);

  /* SENDER ACCEPT — Set sender_request = '1' + Navigate */
  const handleSenderAccept = useCallback(async () => {
    /* ⭐ Stop auto-cancel timer immediately */
    if (autoCancelIntervalRef.current) {
      clearInterval(autoCancelIntervalRef.current);
      autoCancelIntervalRef.current = null;
    }
    autoCancelTriggeredRef.current = true;
    setAutoCancelActive(false);

    setSenderPopupOpen(false);
    setSwipeLeft(0);
    setSwipeRight(0);

    console.log("Sender accepted:", senderData);

    try {
      localStorage.setItem(
        "selected_sender_name",
        senderData.name || ""
      );

      localStorage.setItem(
        "selected_sender_number",
        senderData.number || ""
      );

      localStorage.setItem(
        "selected_sender_address",
        senderData.address || ""
      );

      localStorage.setItem(
        "selected_sender_lat_long",
        senderData.lat_long || ""
      );

      localStorage.setItem(
        "selected_sender_profile",
        senderData.profile_images || ""
      );

      localStorage.setItem(
        "selected_sender_money",
        String(senderData.money || 0)
      );

      localStorage.setItem(
        "selected_sender_chatbox",
        senderData.chatbox || ""
      );

      localStorage.setItem(
        "selected_sender_request",
        senderData.request || ""
      );
    } catch (err) {
      console.warn(
        "Save sender data failed:",
        err
      );
    }

    const success = await updateSenderRequest("1");

    if (success && userId && uuid) {
      setTimeout(() => {
        navigate(
          `/${userId}/${uuid}/navigation_page`
        );
      }, 400);
    }
  }, [
    updateSenderRequest,
    senderData,
    userId,
    uuid,
    navigate,
  ]);

  /* =========================================================
     ⭐ AUTO-CANCEL TIMER
     10 seconds countdown when sender popup opens
  ========================================================= */
  useEffect(() => {
    /* Clear any previous timer */
    if (autoCancelIntervalRef.current) {
      clearInterval(autoCancelIntervalRef.current);
      autoCancelIntervalRef.current = null;
    }

    /* Reset if popup closed */
    if (!senderPopupOpen) {
      setAutoCancelActive(false);
      setAutoCancelTimer(AUTO_CANCEL_DURATION);
      autoCancelTriggeredRef.current = false;
      return;
    }

    /* Popup open → start countdown */
    setAutoCancelTimer(AUTO_CANCEL_DURATION);
    setAutoCancelActive(true);
    autoCancelTriggeredRef.current = false;

    autoCancelIntervalRef.current = setInterval(() => {
      setAutoCancelTimer((prev) => {
        const next = prev - 1;

        /* Reached zero → auto-cancel */
        if (next <= 0) {
          if (autoCancelIntervalRef.current) {
            clearInterval(autoCancelIntervalRef.current);
            autoCancelIntervalRef.current = null;
          }

          if (!autoCancelTriggeredRef.current) {
            autoCancelTriggeredRef.current = true;
            setAutoCancelActive(false);

            setTimeout(() => {
              if (handleSenderCancelRef.current) {
                handleSenderCancelRef.current();
              }
            }, 0);
          }

          return 0;
        }

        return next;
      });
    }, 1000);

    return () => {
      if (autoCancelIntervalRef.current) {
        clearInterval(autoCancelIntervalRef.current);
        autoCancelIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderPopupOpen]);

  /* =======================================================
     UPDATE WORK TO DATABASE
  ======================================================= */

  const updateWorkToDatabase = useCallback(
    async (selectedWork) => {
      if (!userId || !uuid) {
        setMessage({
          type: "error",
          text: "User ID or UUID missing.",
        });
        return false;
      }

      if (!selectedWork) return false;

      setJobUpdating(true);

      try {
        const payload = new FormData();

        payload.append("user_id", String(userId));
        payload.append("uuid", String(uuid));
        payload.append("work", String(selectedWork));

        const response = await fetch(
          API_UPDATE_WORK_URL,
          {
            method: "POST",
            body: payload,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        console.log("UPDATE WORK RESULT:", result);

        if (!result?.success) {
          throw new Error(
            result?.message || "Work update failed."
          );
        }

        setWork(selectedWork);
        setJobType(selectedWork);

        setMessage({
          type: "success",
          text: `Work updated to ${selectedWork}.`,
        });

        setTimeout(() => {
          setMessage({ text: "", type: "" });
        }, 3000);

        return true;
      } catch (error) {
        console.error("Update work error:", error);

        setMessage({
          type: "error",
          text:
            error.message ||
            "Failed to update work.",
        });

        return false;
      } finally {
        setJobUpdating(false);
      }
    },
    [userId, uuid]
  );

  /* =======================================================
     SELECT WORK FROM DROPUP
  ======================================================= */

  const handleSelectWork = useCallback(
    async (selectedWork) => {
      setJobDropupOpen(false);

      if (selectedWork === work) return;

      await updateWorkToDatabase(selectedWork);
    },
    [work, updateWorkToDatabase]
  );

  /* =======================================================
     CLOSE DROPUP WHEN CLICKING OUTSIDE
  ======================================================= */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        !event.target.closest(".partner-job-dropup-wrapper")
      ) {
        setJobDropupOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  /* MENU */
  const handleMenu = () => {
    setMenuOpen(true);
  };

  /* MY WORK */
  const handleDutyClick = () => {
    goTo("/workupload");
  };

  /* CALLING NOTIFICATION */
  const handleCallingNotiClick = () => {
    goTo("/calling_noti");
  };

  /* BOTTOM NAVIGATION */
  const handleHomeNavigation = () => {
    setActiveTab("home");
    goTo("/partnerhome");
  };

  const handleTaskNavigation = () => {
    setActiveTab("task");
    goTo("/partner_booking");
  };

  const handleWalletNavigation = () => {
    setActiveTab("wallet");
    goTo("/partner_wallet");
  };

  /* LOCATION CONDITIONS */
  const hasLocation =
    Number.isFinite(location.latitude) &&
    Number.isFinite(location.longitude);

  /* GOOGLE MAP */
  const mapSrc = hasLocation
    ? `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=17&output=embed`
    : "https://maps.google.com/maps?q=India&z=5&output=embed";

  const mapTitle = hasLocation
    ? "Partner Current Location"
    : "India Map";

  const latitudeText = hasLocation
    ? location.latitude.toFixed(6)
    : "--";

  const longitudeText = hasLocation
    ? location.longitude.toFixed(6)
    : "--";

  /* LOADING SCREEN */
  if (loading) {
    return (
      <div className="partnerEditPage">
        <div className="partnerEditLoading">
          <div className="loadingSpinner"></div>
          <h3>Loading Partner Profile</h3>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  /* RENDER */
  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>
        {/* NAV SLIDER */}
        <PartnerNavSlider
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          navigate={navigate}
        />

        {/* HEADER */}
        <header className={styles.appBar}>
          <div className={styles.brandContainer}>
            <span className={styles.brandBro}>Bro</span>
            <span className={styles.brandBizee}>Bizee</span>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Notifications"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </button>

            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Menu"
              onClick={handleMenu}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </header>

        {/* CONTENT */}
        <div
          className={`${styles.scrollContainer} ${
            dutyState === "on"
              ? styles.scrollContainerOnDuty
              : ""
          }`}
        >
          <div className={styles.partnerHeading}>
            Welcome Partner

            <span
              className={`${styles.partnerStatusBadge} ${
                dutyState === "on"
                  ? styles.partnerStatusOnline
                  : dutyState === "goto"
                    ? styles.partnerStatusWork
                    : styles.partnerStatusOffline
              }`}
            >
              <span className={styles.partnerStatusDot}></span>
              {dutyState === "on"
                ? "Online"
                : dutyState === "goto"
                  ? "IN Work"
                  : "Offline"}
            </span>
          </div>

          {/* DUTY SECTION */}
          <section className={styles.dutySection}>
            <div className={styles.connectorLine} />

            {/* OFF DUTY */}
            <div
              className={styles.dutyNodeWrapper}
              onClick={handleOffDuty}
            >
              <button
                type="button"
                className={`${styles.dutyCircle} ${
                  dutyState === "off" ? styles.activeGreen : ""
                }`}
                aria-label="Off Duty"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#005CA9"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                  <line x1="12" y1="2" x2="12" y2="12" />
                  <line x1="4" y1="4" x2="20" y2="20" />
                </svg>
              </button>

              <span className={styles.dutyText}>OFF DUTY</span>
            </div>

            {/* ON DUTY */}
            <div
              className={styles.dutyNodeWrapper}
              onClick={handleOnDuty}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOnDuty();
                }}
                className={`${styles.dutyCircle} ${
                  dutyState === "on" ? styles.activeGreen : ""
                }`}
                aria-label="On Duty"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#005CA9"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                  <line x1="12" y1="2" x2="12" y2="12" />
                </svg>
              </button>

              <span className={styles.dutyText}>ON DUTY</span>
            </div>

            {/* GO TO WORK */}
            <div
              className={styles.dutyNodeWrapper}
              onClick={handleGoToWork}
            >
              <button
                type="button"
                className={`${styles.dutyCircle} ${
                  dutyState === "goto"
                    ? styles.activeGreenText
                    : ""
                }`}
                aria-label="Go to ONN Work"
              >
                <span className={styles.nodeTextBold}>ONN</span>
                <span className={styles.nodeTextSub}>work</span>
              </button>

              <span className={styles.dutyText}>GO TO</span>
            </div>
          </section>

          {/* MESSAGE POPUP */}
          {message.text && (
            <div
              className={`${styles.statusPopup} ${
                message.type === "success"
                  ? styles.statusPopupSuccess
                  : styles.statusPopupError
              }`}
            >
              <div className={styles.statusPopupIcon}>
                {message.type === "success" ? "✓" : "!"}
              </div>

              <div className={styles.statusPopupContent}>
                <div className={styles.statusPopupTitle}>
                  {message.type === "success"
                    ? "Success"
                    : "Something went wrong"}
                </div>

                <div className={styles.statusPopupMessage}>
                  {message.text}
                </div>
              </div>

              <button
                type="button"
                className={styles.statusPopupClose}
                onClick={() =>
                  setMessage({ text: "", type: "" })
                }
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}

          {/* METRICS */}
          {dutyState !== "on" && (
            <section className={styles.metricsCard}>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>
                  Bookings Count
                </span>
                <span className={styles.metricVal}>
                  00
                </span>
              </div>

              <div className={styles.metricDivider} />

              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>
                  Operator Bill
                </span>
                <span className={styles.metricVal}>
                  00
                </span>
              </div>

              <div className={styles.metricDivider} />

              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>
                  My Work
                </span>
                <button
                  type="button"
                  className={styles.galleryIconWrapper}
                  onClick={handleDutyClick}
                  aria-label="My Work"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#111111"
                    strokeWidth="2"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                    />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </button>
              </div>
            </section>
          )}

          {/* MAP */}
          <section
            className={`${styles.mapContainer} ${
              dutyState === "on"
                ? styles.mapContainerOnDuty
                : ""
            }`}
          >
            <div
              style={{
                position: "relative",
                zIndex: 5,
                padding: "8px 12px",
                display: "none",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              {location.loading && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  📍 Location detect ki ja rahi hai...
                </p>
              )}

              {location.error && (
                <p
                  style={{
                    margin: 0,
                    color: "#d32f2f",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {location.error}
                </p>
              )}

              {hasLocation && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  <span>Lat: {latitudeText}</span>
                  <span>Long: {longitudeText}</span>
                </div>
              )}
            </div>

            <div
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: "280px",
                overflow: "hidden",
                borderRadius: "inherit",
                background: "#e9ebee",
              }}
            >
              <iframe
                key={mapKey}
                title={mapTitle}
                src={mapSrc}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={() => setMapLoading(false)}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                  display: "block",
                  borderRadius: "inherit",
                  transform: "scale(1.001)",
                  animation:
                    "broBizeeMapFadeIn 0.7s ease-out",
                }}
              />

              {(mapLoading || location.loading) && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                    background: "rgba(255,255,255,0.28)",
                    backdropFilter: "blur(2px)",
                    WebkitBackdropFilter: "blur(2px)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "10px",
                      padding: "14px 18px",
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.88)",
                      boxShadow:
                        "0 10px 30px rgba(0,0,0,0.12)",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        border:
                          "3px solid rgba(0,92,169,0.18)",
                        borderTopColor: "#005CA9",
                        borderRadius: "50%",
                        animation:
                          "broBizeeSpin 0.8s linear infinite",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#005CA9",
                      }}
                    >
                      Finding your location...
                    </span>
                  </div>
                </div>
              )}

              {hasLocation && (
                <div
                  style={{
                    position: "absolute",
                    right: "14px",
                    bottom: "14px",
                    zIndex: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "999px",
                    background: "rgba(255,255,255,0.94)",
                    boxShadow:
                      "0 8px 24px rgba(0,0,0,0.15)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#005CA9",
                  }}
                >
                  <span
                    style={{
                      position: "relative",
                      width: "10px",
                      height: "10px",
                      display: "inline-block",
                      borderRadius: "50%",
                      background: "#22c55e",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        inset: "-4px",
                        borderRadius: "50%",
                        border: "2px solid #22c55e",
                        animation:
                          "broBizeeLocationPulse 1.6s ease-out infinite",
                      }}
                    />
                  </span>

                  {dutyState === "on"
                    ? "Live Location"
                    : "Current Location"}
                </div>
              )}
            </div>

            {locationError && (
              <div className={styles.locationError}>
                {locationError}
              </div>
            )}

            {currentLocation && (
              <div className={styles.locationInfo}>
                <span className={styles.locationDot} />
                <span>
                  GPS Accuracy:{" "}
                  {Math.round(currentLocation.accuracy)}m
                </span>
              </div>
            )}

          </section>

          {/* CONTROL ROW */}
          {dutyState !== "on" && (
            <section className={styles.controlRow}>
              <div className={styles.controlCol}>
                <span className={styles.controlHeading}>
                  Switch Your Job
                </span>

                <div
                  className="partner-job-dropup-wrapper"
                  style={{ position: "relative" }}
                >
                  <button
                    type="button"
                    className={styles.jobPillBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setJobDropupOpen((prev) => !prev);
                    }}
                    disabled={jobUpdating}
                    aria-expanded={jobDropupOpen}
                  >
                    <span>
                      {jobUpdating
                        ? "Updating..."
                        : work || "—"}
                    </span>

                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#FF6B00"
                      strokeWidth="2.5"
                      style={{
                        transform: jobDropupOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      <polyline points="18 15 12 9 6 15" />
                    </svg>
                  </button>

                  {jobDropupOpen && (
                    <div
                      className={styles.jobDropupMenu}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className={styles.jobDropupHeader}
                      >
                        Select Work
                      </div>

                      <div
                        className={styles.jobDropupList}
                      >
                        {jobOptions.map((option) => {
                          const isActive =
                            option === work;

                          return (
                            <button
                              key={option}
                              type="button"
                              className={`${styles.jobDropupItem} ${
                                isActive
                                  ? styles.jobDropupItemActive
                                  : ""
                              }`}
                              onClick={() =>
                                handleSelectWork(option)
                              }
                              disabled={jobUpdating}
                            >
                              <span>{option}</span>

                              {isActive && (
                                <span
                                  className={
                                    styles.jobDropupCheck
                                  }
                                >
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.controlCol}>
                <span className={styles.controlHeading}>
                  Mode Of Transport
                </span>

                <div className={styles.transportRow}>
                  <svg
                    width="34"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#111111"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="5.5" cy="17.5" r="3.5" />
                    <circle cx="18.5" cy="17.5" r="3.5" />
                    <path d="M15 6h2l2 4" />
                    <polyline points="12 17.5 12 11 8 6 4 6" />
                    <polyline points="12 11 15 6" />
                  </svg>

                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#005CA9"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>

            </section>
          )}
        </div>

        {/* =================================================
    BOTTOM NAVIGATION (NEW — camelCase classes)
================================================= */}
<div className={styles.bottomNavWrapper}>
  <nav
    className={styles.bottomNav}
    aria-label="Bottom Navigation"
  >
    {/* HOME */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "home" ? styles.active : ""
      }`}
      onClick={handleHomeNavigation}
      aria-label="Home"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>

      <span className={styles.navLabel}>Home</span>
    </button>

    {/* TASKS */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "task" ? styles.active : ""
      }`}
      onClick={handleTaskNavigation}
      aria-label="Tasks"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" />
        <path d="M8 11h8" />
        <path d="M8 15h5" />
      </svg>

      <span className={styles.navLabel}>Tasks</span>
    </button>

    {/* WALLET */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "wallet" ? styles.active : ""
      }`}
      onClick={handleWalletNavigation}
      aria-label="Wallet"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="6" width="20" height="12" rx="3" />
        <path d="M18 12h.01" />
      </svg>

      <span className={styles.navLabel}>Wallet</span>
    </button>
  </nav>
</div>

        {/* SENDER POPUP */}
        {senderPopupOpen && (
          <div className={styles.senderPopupOverlay}>
            <div className={styles.senderPopupCard}>
              <div className={styles.senderPopupHandle} />

              {/* Header */}
              <div className={styles.senderPopupHeader}>
                <div className={styles.senderPopupAvatarWrap}>
                  {senderData.profile_images ? (
                    <img
                      src={makeImageUrl(senderData.profile_images)}
                      alt={senderData.name || "Sender"}
                      className={styles.senderPopupAvatar}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className={styles.senderPopupAvatarFallback}>
                      {(senderData.name || "S")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                  )}

                  <span className={styles.senderPopupPulseDot} />
                </div>

                <div className={styles.senderPopupHeaderInfo}>
                  <span className={styles.senderPopupEyebrow}>
                    NEW REQUEST
                  </span>

                  <h3 className={styles.senderPopupName}>
                    {senderData.name || "Unknown Sender"}
                  </h3>

                  <span className={styles.senderPopupNumber}>
                    📞 {senderData.number || "No number"}
                  </span>
                </div>
              </div>

              {/* Grid */}
              <div className={styles.senderPopupGrid}>
                <div className={styles.senderPopupTile}>
                  <span className={styles.senderPopupTileLabel}>
                    📍 Address
                  </span>
                  <span className={styles.senderPopupTileValueSmall}>
                    {senderData.address || "—"}
                  </span>
                </div>

                <div className={styles.senderPopupTile}>
                  <span className={styles.senderPopupTileLabel}>
                    💬 Chatbox
                  </span>
                  <span className={styles.senderPopupTileValueSmall}>
                    {senderData.chatbox || "—"}
                  </span>
                </div>
              </div>

              {/* ⭐ AUTO-CANCEL COUNTDOWN BANNER */}
              {autoCancelActive && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    margin: "0 2px 10px",
                    padding: "10px 14px",
                    borderRadius: "14px",
                    background:
                      autoCancelTimer <= 3
                        ? "linear-gradient(135deg, #fff1f1, #ffe4e4)"
                        : "linear-gradient(135deg, #eef6ff, #f6fbff)",
                    border:
                      autoCancelTimer <= 3
                        ? "1px solid #ffb4b4"
                        : "1px solid #cfe3ff",
                    transition:
                      "background 0.3s ease, border 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        minWidth: "32px",
                        borderRadius: "50%",
                        background:
                          autoCancelTimer <= 3
                            ? "linear-gradient(135deg, #ff4d4d, #d93636)"
                            : "linear-gradient(135deg, #005CA9, #0077d6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 900,
                        fontSize: "14px",
                        boxShadow:
                          autoCancelTimer <= 3
                            ? "0 6px 14px rgba(255, 77, 77, 0.35)"
                            : "0 6px 14px rgba(0, 92, 169, 0.25)",
                        animation:
                          autoCancelTimer <= 3
                            ? "pulseRedBadge 0.9s ease-in-out infinite"
                            : "none",
                      }}
                    >
                      {autoCancelTimer}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 900,
                          letterSpacing: "0.4px",
                          color:
                            autoCancelTimer <= 3
                              ? "#d93636"
                              : "#005CA9",
                        }}
                      >
                        {autoCancelTimer <= 3
                          ? "⚠️ Hurry Up!"
                          : "⏱️ Request Expires In"}
                      </span>

                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 600,
                          color: "#4d6070",
                        }}
                      >
                        {autoCancelTimer <= 3
                          ? "Accept now or request will cancel"
                          : "Slide to Accept before timer ends"}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      minWidth: "44px",
                      textAlign: "right",
                      fontSize: "13px",
                      fontWeight: 900,
                      color:
                        autoCancelTimer <= 3
                          ? "#d93636"
                          : "#005CA9",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {autoCancelTimer}s
                  </div>
                </div>
              )}

              {/* Swipe Row */}
              <div className={styles.senderPopupSwipeRow}>
                <div
                  className={styles.senderPopupSwipeTrack}
                  onMouseDown={handleCancelSwipeStart}
                  onTouchStart={handleCancelSwipeStart}
                >
                  <div
                    className={styles.senderPopupSwipeFillCancel}
                    style={{ width: `${swipeLeft * 100}%` }}
                  />

                  <div
                    className={styles.senderPopupSwipeLabel}
                    style={{
                      transform: `translateX(-${swipeLeft * 30}px)`,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                    <span>Slide to Cancel</span>
                  </div>
                </div>

                <div
                  className={styles.senderPopupSwipeTrack}
                  onMouseDown={handleAcceptSwipeStart}
                  onTouchStart={handleAcceptSwipeStart}
                >
                  <div
                    className={styles.senderPopupSwipeFillAccept}
                    style={{ width: `${swipeRight * 100}%` }}
                  />

                  <div
                    className={styles.senderPopupSwipeLabel}
                    style={{
                      transform: `translateX(${swipeRight * 30}px)`,
                    }}
                  >
                    <span>Slide to Accept</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ANIMATIONS */}
      <style>
        {`
          @keyframes broBizeeMapFadeIn {
            from { opacity: 0; transform: scale(1.025); }
            to   { opacity: 1; transform: scale(1.001); }
          }

          @keyframes broBizeeSpin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }

          @keyframes broBizeeLocationPulse {
            0%   { transform: scale(0.6); opacity: 0.9; }
            70%  { transform: scale(1.8); opacity: 0; }
            100% { transform: scale(1.8); opacity: 0; }
          }

          @keyframes pulseRedBadge {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 6px 14px rgba(255, 77, 77, 0.35);
            }
            50% {
              transform: scale(1.08);
              box-shadow: 0 8px 22px rgba(255, 77, 77, 0.55);
            }
          }
        `}
      </style>
    </div>
  );
}